import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Post } from '../../types';

interface CalendarEvent {
  id: string;
  title: string;
  start: string;
  end: string;
  postId: string;
  platform: string;
}

interface SchedulingState {
  scheduledPosts: Post[];
  calendarEvents: CalendarEvent[];
  selectedDate: string | null;
  calendarView: 'month' | 'week' | 'day';
  loading: boolean;
  error: string | null;
}

const initialState: SchedulingState = {
  scheduledPosts: [],
  calendarEvents: [],
  selectedDate: null,
  calendarView: 'month',
  loading: false,
  error: null,
};

const schedulingSlice = createSlice({
  name: 'scheduling',
  initialState,
  reducers: {
    setScheduledPosts: (state, action: PayloadAction<Post[]>) => {
      state.scheduledPosts = action.payload;
      // Convert posts to calendar events
      state.calendarEvents = action.payload
        .filter(post => post.scheduled_time)
        .map(post => ({
          id: `post-${post.id}`,
          title: post.topic,
          start: post.scheduled_time!,
          end: post.scheduled_time!,
          postId: post.id,
          platform: post.platforms?.join(', ') || 'multiple',
        }));
    },
    setSelectedDate: (state, action: PayloadAction<string | null>) => {
      state.selectedDate = action.payload;
    },
    setCalendarView: (state, action: PayloadAction<'month' | 'week' | 'day'>) => {
      state.calendarView = action.payload;
    },
    addScheduledPost: (state, action: PayloadAction<Post>) => {
      const existingIndex = state.scheduledPosts.findIndex(p => p.id === action.payload.id);
      if (existingIndex >= 0) {
        state.scheduledPosts[existingIndex] = action.payload;
      } else {
        state.scheduledPosts.push(action.payload);
      }
    },
    removeScheduledPost: (state, action: PayloadAction<string>) => {
      state.scheduledPosts = state.scheduledPosts.filter(p => p.id !== action.payload);
      state.calendarEvents = state.calendarEvents.filter(e => e.postId !== action.payload);
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
});

export const {
  setScheduledPosts,
  setSelectedDate,
  setCalendarView,
  addScheduledPost,
  removeScheduledPost,
  setLoading,
  setError,
} = schedulingSlice.actions;

export default schedulingSlice.reducer; 